import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Box, FormControl } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useDispatch, useSelector } from "react-redux";
import UpdatedBadge from "./UpdatedBadge";
import {
  answersActions,
  RootStateAnswers,
} from "../../../questionnaire/store/answers-slice";
import { personalInfoType } from "../../store/helper";
import {
  PhoneInput,
  defaultCountries,
  parseCountry,
} from "react-international-phone";
import "react-international-phone/style.css";
import { PhoneNumberUtil, PhoneNumberType } from "google-libphonenumber";
import {
  getStoredPhoneValidity,
  toInternationalPhone,
} from "../../../../common/utils/phone";
import FieldHintMessage from "./FieldHintMessage";

const phoneUtil = PhoneNumberUtil.getInstance();

// react-international-phone caps input to the country's mask length. Countries
// without a built-in format (e.g. Greece) fall back to `defaultMask`, so a
// fixed mask lets them overflow. Build the fallback mask per-country from the
// example number's national length — this caps format-less countries at their
// real length and doesn't affect countries that already have a format.
const nationalLenCache: Record<string, number> = {};
const maskForRegion = (region?: string): string => {
  const r = (region || "US").toUpperCase();
  if (nationalLenCache[r] == null) {
    const lengths: number[] = [];
    const add = (getNumber: () => any) => {
      try {
        const n = getNumber();
        if (n) lengths.push(String(n.getNationalNumber()).length);
      } catch {
        /* type not defined for region */
      }
    };
    // Max across mobile / fixed-line / generic so we never cut a valid mobile
    // (e.g. DE mobile is longer than the fixed-line example).
    add(() => phoneUtil.getExampleNumberForType(r, PhoneNumberType.MOBILE));
    add(() => phoneUtil.getExampleNumberForType(r, PhoneNumberType.FIXED_LINE));
    add(() => phoneUtil.getExampleNumber(r));
    nationalLenCache[r] = lengths.length ? Math.max(...lengths) : 15;
  }
  return ".".repeat(nationalLenCache[r]);
};

/**
 * Two phone values are the same number if their digits match — formatting and a
 * leading dial code are presentation, not data. Used for dirty-checking so a
 * re-format never reads as an edit.
 */
const samePhoneNumber = (
  a: unknown,
  b: unknown,
  dialCode?: string
): boolean => {
  const dial = String(dialCode ?? "").replace(/\D/g, "");
  const normalise = (v: unknown) => {
    const d = String(v ?? "").replace(/\D/g, "");
    // Either side may carry the country's dial code; drop it so "12024561414"
    // and "2024561414" compare equal. Deliberately not a suffix match — that
    // would read a genuine edit as unchanged whenever the new number happened
    // to end with the old one.
    return dial && d.startsWith(dial) ? d.slice(dial.length) : d;
  };
  return normalise(a) === normalise(b);
};

const regionOf = (phone: string): string => {
  try {
    return (
      phoneUtil.getRegionCodeForNumber(phoneUtil.parseAndKeepRawInput(phone)) ||
      "US"
    );
  } catch {
    return "US";
  }
};

// Styled phone input container that matches the design system
const StyledPhoneInputContainer = styled.div<{
  $is_focused?: boolean;
  theme: Theme;
}>`
  .react-international-phone-input-container {
    height: 56px;
    border-radius: 8px;
    background-color: #fff;
    border: 1px solid
      ${(props) =>
        props.$is_focused
          ? props.theme.colors_ds.PRIMARY_ICON_MAIN
          : props.theme.colors_ds.NEUTRAL_BORDER_WEAK};
    display: flex;
    align-items: center;
    padding: 0 12px;
    width: 100%;

    &:hover {
      border-color: ${(props) => props.theme.colors_ds.NEUTRAL_BORDER_DEFAULT};
    }

    &:focus-within {
      border-color: ${(props) => props.theme.colors_ds.PRIMARY_ICON_MAIN};
    }
  }

  .react-international-phone-input {
    font-family: var(--font-Medium), sans-serif;
    font-size: 12px;
    border: none;
    background: none;
    padding: 24px 0 8px 8px;
    outline: none;
    flex: 1;
    height: 100%;
  }

  /* Style the country selector dropdown */
  .react-international-phone-country-selector-button {
    margin-top: 16px;
    margin-left: -4px;
    display: flex;
    align-items: center;
    position: relative;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    height: 24px;
  }

  /* Style the dropdown menu */
  .react-international-phone-country-selector-dropdown {
    background-color: white;
    margin-top: 10px;
    margin-left: -12px;
    border: 1px solid ${(props) => props.theme.colors_ds.PRIMARY_ICON_MAIN};
    border-radius: 8px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    /* Fit the field, not a fixed 453px — otherwise it overflows the narrow
       grid cell in the Basic Information modal and covers neighbouring fields. */
    width: 300px;
    max-width: calc(100vw - 48px);
    max-height: 300px;
    overflow-y: auto;
    z-index: 100 !important;
    position: absolute !important;
  }

  /* Hide all arrow elements */
  .react-international-phone-country-selector-button span:last-child,
  .react-international-phone-country-selector-button svg {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border: none !important;
    opacity: 0 !important;
    visibility: hidden !important;
  }

  position: relative;

  label {
    position: absolute;
    top: 8px;
    left: 12px;
    font-family: var(--font-Medium), sans-serif;
    font-size: 10px;
    color: ${(props) =>
      props.$is_focused
        ? props.theme.colors_ds.PRIMARY_ICON_MAIN
        : props.theme.colors_ds.NEUTRAL_TEXT_WEAK};
    background-color: transparent;
    pointer-events: none;
    z-index: 2;
    transition: color 0.2s ease-in-out;
  }
`;


interface StyledPhoneInputProps {
  category: string;
  label: string;
  openPopperId: string | null;
  setOpenPopperId: React.Dispatch<React.SetStateAction<string | null>>;
}

const StyledPhoneInput: React.FC<StyledPhoneInputProps> = ({
  category,
  label,
  setOpenPopperId,
}) => {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const dispatch = useDispatch();

  const answers = useSelector((state: RootStateAnswers) => state.answers);
  const storedPhone = answers.personalInfoAnswers[category] || "";
  // `countryCode` holds the ISO2 country (e.g. "US", "AS") — the same code
  // react-international-phone uses. We keep the national number in `phone` and
  // reconstruct the full international value for the input to render the flag +
  // number, staying backward-compatible with legacy full "+.." phone values.
  const storedIso2 = answers.personalInfoAnswers[personalInfoType.countryCode];
  const value = toInternationalPhone(storedPhone, storedIso2);

  // Same verdict the Save gates use (ProfileFormContext / BiometricTab), so the
  // hint and Save can never disagree. Only flag "invalid" once the field is
  // blurred so we don't nag while one is still being typed.
  const phoneValid = getStoredPhoneValidity(storedPhone, storedIso2);
  const showInvalid = phoneValid === false && !focused;

  // Region drives the fallback mask length so format-less countries can't be
  // typed past their valid length. Seeded from the stored value's country.
  const [region, setRegion] = useState<string>(() => regionOf(value));

  // `PhoneInput` keeps its own internal value and re-emits it after a re-render
  // rather than adopting a new `value` prop. So a parent-driven revert (Cancel)
  // was immediately overwritten by the stale internal value and the form could
  // never go clean. Remount the widget when `value` changes from OUTSIDE —
  // identified by not matching the last value we emitted ourselves — so it
  // re-initialises from the restored number.
  const lastEmittedRef = useRef<string | null>(null);
  const [externalSync, setExternalSync] = useState(0);
  useEffect(() => {
    if (
      lastEmittedRef.current !== null &&
      !samePhoneNumber(value, lastEmittedRef.current)
    ) {
      lastEmittedRef.current = value;
      setExternalSync((n) => n + 1);
    }
  }, [value]);
  const dynamicMask = maskForRegion(region);

  const handleChange = (enteredValue: string, country?: any) => {
    // Absolute E.164 ceiling (max 15 digits incl. country code).
    if (enteredValue.replace(/\D/g, "").length > 15) {
      return;
    }

    // Persist the ISO2 country in `countryCode` and the national number (no dial
    // code prefix) in `phone`.
    const iso2 = country?.iso2 ? String(country.iso2).toUpperCase() : "";
    const dialPrefix = country?.dialCode ? `+${country.dialCode}` : "";
    const stripped =
      dialPrefix && enteredValue.startsWith(dialPrefix)
        ? enteredValue.slice(dialPrefix.length).trim()
        : enteredValue;
    const newValue: string | null = stripped === "" ? null : stripped;

    dispatch(
      answersActions.updatePersonalInfoAnswers({
        [category]: newValue,
        [personalInfoType.countryCode]: iso2 || null,
      })
    );

    // Compare digits, not strings. We store the national number with the dial
    // code stripped and the widget's own punctuation ("(202) 456-1414"), while
    // the server's copy uses its own format — so a character comparison says
    // "changed" even when the number is identical.
    //
    // That mattered beyond a stray dot: `PhoneInput` re-emits its normalised
    // value whenever `value` changes, so Cancel restoring the server's format
    // immediately bounced back through here and re-marked the field dirty. The
    // revert never stuck and the save bar could never clear, no matter how many
    // times Cancel was pressed.
    if (
      samePhoneNumber(
        newValue,
        answers.personalInfoInitialAnswers[category],
        country?.dialCode
      )
    ) {
      if (answers.personalInfoModifiedFields.indexOf(category) !== -1) {
        dispatch(answersActions.removePersonalInfoModifiedFields(category));
      }
    } else {
      if (answers.personalInfoModifiedFields.indexOf(category) === -1) {
        dispatch(answersActions.addPersonalInfoModifiedFields(category));
      }
    }
  };

  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={(e) => e.preventDefault()}
      sx={{
        position: "relative",
        width: "100%",
      }}
    >
      <FormControl fullWidth>
        <StyledPhoneInputContainer $is_focused={focused} theme={theme}>
          <label>
            {label}
            {answers.personalInfoModifiedFields.indexOf(category) !== -1 && (
              <UpdatedBadge />
            )}
          </label>
          <PhoneInput
            key={`${region}-${externalSync}`}
            // Follow the selected region so the `key`-driven remount defaults to
            // the country the user just picked (not hardcoded US) — otherwise
            // selecting a country with no number typed snaps the flag back to US.
            defaultCountry={region.toLowerCase() as any}
            value={value}
            onChange={(phoneNumber, { country }) => {
              lastEmittedRef.current = phoneNumber;
              const iso2 = country?.iso2?.toUpperCase();
              if (iso2 && iso2 !== region) setRegion(iso2);
              handleChange(phoneNumber, country);
            }}
            onFocus={() => {
              setFocused(true);
              setOpenPopperId(null);
            }}
            onBlur={() => setFocused(false)}
            inputStyle={{ fontSize: "12px" }}
            countrySelectorStyleProps={{
              buttonStyle: {
                fontSize: "12px",
                background: "none",
                border: "none",
                justifyContent: "flex-start",
                minWidth: "32px",
                paddingRight: 0,
                overflow: "hidden",
                width: "32px",
              },
            }}
            forceDialCode={true}
            hideDropdown={false}
            disableDialCodeAndPrefix={false}
            prefix="+"
            defaultMask={dynamicMask}
          />
        </StyledPhoneInputContainer>

        {showInvalid && (
          <FieldHintMessage tone="danger">
            Enter a valid phone number
          </FieldHintMessage>
        )}
      </FormControl>
    </Box>
  );
};

export default StyledPhoneInput;
